import type { BookingSessionResponse } from '@massilia/queries'

import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import { TRIP_TYPE_LABELS } from '@/utils/trip-labels'

import { SessionInstallmentTimeline } from './session-installment-timeline'

interface TripHeaderProps {
  session: BookingSessionResponse
  /** Frise échéancier sous le titre — affichée uniquement à l'étape informations. */
  showTimeline?: boolean
}

export function TripHeader({ session, showTimeline = true }: TripHeaderProps) {
  const flightDetails = session.flightDetails
  const { arrival, departure } = getAirportDisplayNames(session)
  const tripLabel = TRIP_TYPE_LABELS[flightDetails.tripType] ?? flightDetails.tripType
  const passengerCount = getPassengerCount(session)
  const departureFormatted = formatTripDate(flightDetails.departureDate)
  const lastDate = flightDetails.returnDate ?? getLastJourneyDate(session)
  const returnFormatted =
    flightDetails.tripType !== 'one-way' && lastDate ? formatTripDate(lastDate) : undefined

  // Diagnostic: log why the installment timeline is hidden so non-eligible cases are visible in DevTools.
  // Skipped when showTimeline is false (masquage volontaire des étapes après informations).
  /* eslint-disable no-console */
  if (import.meta.env.DEV && showTimeline && !session.installmentInfo?.available) {
    console.debug(
      '[InstallmentTimeline] hidden — reason:',
      session.installmentInfo?.reason ?? 'no installmentInfo'
    )
  } else if (
    import.meta.env.DEV &&
    showTimeline &&
    (session.installmentInfo?.minimumDepositCents == null || !session.paymentDeadline)
  ) {
    console.debug('[InstallmentTimeline] hidden — missing data:', {
      minimumDepositCents: session.installmentInfo?.minimumDepositCents,
      paymentDeadline: session.paymentDeadline,
    })
  }
  /* eslint-enable no-console */

  return (
    <div className="flex flex-col gap-2">
      <div className="text-gray-light-600 flex items-center gap-2 text-xs font-medium">
        <span>{tripLabel}</span>
        <span>&bull;</span>
        <span>
          {passengerCount} passager{passengerCount > 1 ? 's' : ''}
        </span>
        <span>&bull;</span>
        <span>
          {departureFormatted}
          {returnFormatted && ` - ${returnFormatted}`}
        </span>
      </div>
      <h1 className="text-gray-light-900 text-2xl font-semibold">
        Voyage de {departure} vers {arrival}
      </h1>
      {showTimeline && <SessionInstallmentTimeline session={session} />}
    </div>
  )
}

function formatTripDate(isoDate: string): string {
  try {
    return format(new Date(isoDate), 'EEE d MMM', { locale: fr })
  } catch {
    return isoDate
  }
}

function getAirportDisplayNames(session: BookingSessionResponse): {
  arrival: string
  departure: string
} {
  const journeys = session.priceData?.journeys
  if (journeys && journeys.length > 0) {
    const firstJourney = journeys[0]
    const segments = firstJourney.segments

    if (segments.length > 0) {
      const departure = segments[0].departure.airport
      const arrival = segments[segments.length - 1].arrival.airport

      if (departure && arrival) {
        return { arrival, departure }
      }
    }
  }

  // Fallback: use flightDetails search labels
  return {
    arrival: session.flightDetails.destination,
    departure: session.flightDetails.origin,
  }
}

function getLastJourneyDate(session: BookingSessionResponse): null | string {
  const journeys = session.priceData?.journeys
  if (!journeys || journeys.length === 0) return null
  const lastJourney = journeys[journeys.length - 1]
  const segments = lastJourney.segments
  if (segments.length === 0) return null
  return segments[segments.length - 1].arrival.date
}

function getPassengerCount(session: BookingSessionResponse): number {
  if (session.passengers && session.passengers.length > 0) {
    return session.passengers.length
  }
  const counts = session.flightDetails.passengersCount
  return Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0)
}
